import type { RecordKey } from './identity.ts';
import { identity, isString } from './identity.ts';

export function capitalize<T extends string>(value: T): Capitalize<T> {
  return (value.length ? value[0].toLocaleUpperCase() + value.slice(1) : '') as Capitalize<T>;
}

export function uncapitalize(value: string) {
  return value.length ? value[0].toLocaleLowerCase() + value.slice(1) : '';
}

export function pluralize(quantityType: string, count?: number) {
  if (count === 1) {
    return quantityType;
  }
  if (quantityType.endsWith('y')) {
    return `${quantityType.slice(0, -1)}ies`;
  }
  if (quantityType.endsWith('s')) {
    return `${quantityType}es`;
  }
  return `${quantityType}s`;
}

const knownCapitalizations: Partial<Record<string, string>> = {
  id: 'ID',
} as const;

export function capitalizeForLabel(value: string) {
  return knownCapitalizations[value] ?? capitalize(value);
}

export function quantity(quantityType: string, count: number) {
  return `${count} ${pluralize(quantityType, count)}`;
}

export function propToLabel(prop: RecordKey) {
  if (!isString(prop)) {
    prop = String(prop);
  }
  return prop
    .replaceAll(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[ _-]/)
    .map(capitalizeForLabel)
    .join(' ');
}

export function labelToProp(label: string) {
  return label
    .split(' ')
    .map((s, i) => (i === 0 ? uncapitalize(s) : capitalize(s)))
    .join('');
}

export function nonEmpty(s: string | undefined | null, d: string) {
  return (s ?? d) || d;
}

export function removePrefix(s: string, prefix: string) {
  return s.startsWith(prefix) ? s.slice(prefix.length) : undefined;
}

export function maybeRemovePrefix(s: string, prefix: string) {
  return removePrefix(s, prefix) ?? s;
}

export function removeSuffix(s: string, suffix: string) {
  return s.endsWith(suffix) ? s.slice(0, -suffix.length) : undefined;
}

export function maybeRemoveSuffix(s: string, suffix: string) {
  return removeSuffix(s, suffix) ?? s;
}

export function stringCompare(a: string, b: string) {
  if (a < b) {
    return -1;
  } else if (b < a) {
    return 1;
  } else {
    return 0;
  }
}

export function nocaseCompare(a: string, b: string) {
  // https://stackoverflow.com/q/2140627/8107589
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

export function nocaseEqual(a: string | undefined | null, b: string | undefined | null) {
  // eslint-disable-next-line eqeqeq
  return b != null && a?.localeCompare(b, undefined, { sensitivity: 'base' }) === 0;
}

export function nocaseIncludes(
  needle: string | undefined | null,
  haystack: string | undefined | null,
) {
  return (
    // eslint-disable-next-line eqeqeq
    needle != null && (haystack?.toLocaleLowerCase().includes(needle.toLocaleLowerCase()) ?? false)
  );
}

// Returns the quality of matching |target| against |candidate|. Higher numbers are better,
// with 0 meaning no match. Quality values may change over time and should not be saved.
function fuzzyMatchQuality<T extends string>(fragment: string, candidate: T): number {
  let index = candidate.indexOf(fragment);
  if (index === 0) {
    return candidate.length === fragment.length ? 10 : 9;
  } else if (index > 0) {
    return index === candidate.length - fragment.length ? 8 : 7;
  }
  index = candidate.toLocaleLowerCase().indexOf(fragment.toLocaleLowerCase());
  if (index === 0) {
    return candidate.length === fragment.length ? 5 : 4;
  } else if (index > 0) {
    return index === candidate.length - fragment.length ? 3 : 2;
  }
  return 0;
}

// Finds the member(s) of |haystack| which are the best match for |needle|.
export function fuzzyMatch<T extends string>(needle: string, haystack: readonly T[]): T[];
export function fuzzyMatch<T>(needle: string, haystack: readonly T[], keyOf: (v: T) => string): T[];
export function fuzzyMatch<T>(
  needle: string,
  haystack: readonly T[],
  keyOf?: (v: T) => string,
): T[] {
  keyOf ??= identity as (v: T) => string;
  let matches: number[] = [];
  let quality = 0;
  for (let i = 0; i < haystack.length; ++i) {
    const q = fuzzyMatchQuality(needle, keyOf(haystack[i]));
    if (q > quality) {
      quality = q;
      matches = [i];
    } else if (q > 0 && q === quality) {
      matches.push(i);
    }
  }
  return matches.map((i) => haystack[i]);
}
